import {
  Box,
  Flex,
  Link,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { Trans, useTranslation } from 'react-i18next';

/**
 * Solution Trial Content Component
 * Displays billing model selection inside the Credit Team's solution trial modal
 * This matches the design from Vision One Credit System
 *
 * Note: This component is rendered outside the main React tree via ReactDOM.render()
 * but is wrapped with TonicProvider to enable color mode and style support.
 */

interface PricingItem {
  packageKey: string;
  billingModelKey: string;
  credits: number;
}

const pricingData: PricingItem[] = [
  {
    packageKey: 'welcome.solutionTrial.pricing.package.emdr',
    billingModelKey: 'welcome.solutionTrial.pricing.billingModel',
    credits: 0.42,
  },
  {
    packageKey: 'welcome.solutionTrial.pricing.package.core',
    billingModelKey: 'welcome.solutionTrial.pricing.billingModel',
    credits: 2.08,
  },
  {
    packageKey: 'welcome.solutionTrial.pricing.package.essentials',
    billingModelKey: 'welcome.solutionTrial.pricing.billingModel',
    credits: 4.17,
  },
  {
    packageKey: 'welcome.solutionTrial.pricing.package.pro',
    billingModelKey: 'welcome.solutionTrial.pricing.billingModel',
    credits: 8.75,
  },
];

const SolutionTrialContent = () => {
  const { t } = useTranslation('welcome');
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  return (
    <Box>
      {/* Pricing Table */}
      <Box>
        {/* Table Header */}
        <Flex
          bg={colorStyle.background.secondary}
          p="12px 16px"
          borderTop="1px solid"
          borderBottom="1px solid"
          borderColor={colorStyle.background.tertiary}
        >
          <Box flex="2" pr="16px">
            <Text
              color={colorStyle.color.secondary}
              fontSize="14px"
              fontWeight="600"
              maxWidth={200}
            >
              {t('welcome.solutionTrial.pricing.table.header.package')}
            </Text>
          </Box>
          <Box flex="1" pr="16px">
            <Text
              color={colorStyle.color.secondary}
              fontSize="14px"
              fontWeight="600"
            >
              {t('welcome.solutionTrial.pricing.table.header.billingModel')}
            </Text>
          </Box>
          <Box flex="1">
            <Text
              color={colorStyle.color.secondary}
              fontSize="14px"
              fontWeight="600"
            >
              {t('welcome.solutionTrial.pricing.table.header.pricing')}
            </Text>
          </Box>
        </Flex>

        {/* Table Rows */}
        {pricingData.map((item, index) => (
          <Flex
            key={index}
            p="12px 16px"
            borderBottom="1px solid"
            borderColor={colorStyle.background.tertiary}
          >
            <Box flex="2" pr="16px">
              <Text color={colorStyle.color.primary} fontSize="14px">
                {t(item.packageKey)}
              </Text>
            </Box>
            <Box flex="1" pr="16px">
              <Text color={colorStyle.color.primary} fontSize="14px">
                {t(item.billingModelKey)}
              </Text>
            </Box>
            <Box flex="1">
              <Text color={colorStyle.color.primary} fontSize="14px">
                {t('welcome.solutionTrial.pricing.creditsPerUser', {
                  credits: item.credits,
                })}
              </Text>
            </Box>
          </Flex>
        ))}
      </Box>

      {/* Bottom Notice */}
      <Box mt="16px">
        <Text
          color={colorStyle.color.secondary}
          fontSize="14px"
          lineHeight="20px"
        >
          <Trans i18nKey="welcome.solutionTrial.pricing.notice">
            To avoid a disruption in service, make sure you have enough
            available credits or a pay-as-you-go contract when the trial
            expires. For more information,
            <Link
              href="https://resources.trendmicro.com/vision-one-callback.html"
              target="_blank"
            >
              contact your sales representative
            </Link>
            .
          </Trans>
        </Text>
      </Box>
    </Box>
  );
};

export default SolutionTrialContent;
